import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './ShowUsYourTdsPage.module.css';
import {
  claimSuytDraftSlot,
  deleteSuytPrediction,
  fetchSuytDraft,
  resetSuytDraftSlots,
  submitSuytPrediction,
  submitSuytResults,
} from './suytApi';

const ADMIN_STORAGE_KEY = 'suyt-admin-key';
const ORDINAL = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

const ShowUsYourTdsPage = () => {
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [ouScore, setOuScore] = useState('');
  const [osuScore, setOsuScore] = useState('');
  const [showCommissioner, setShowCommissioner] = useState(false);
  const [adminKey, setAdminKey] = useState(() => (
    window.sessionStorage.getItem(ADMIN_STORAGE_KEY) || ''
  ));
  const [ouActual, setOuActual] = useState('');
  const [osuActual, setOsuActual] = useState('');

  const applyState = useCallback((nextState) => {
    setState(nextState);
    if (nextState?.results) {
      setOuActual(String(nextState.results.ouActual));
      setOsuActual(String(nextState.results.osuActual));
    }
  }, []);

  const loadState = useCallback(async () => {
    try {
      const response = await fetchSuytDraft();
      applyState(response.data);
      setLoadError('');
    } catch (error) {
      setLoadError(getErrorMessage(error, 'Unable to load the draft challenge.'));
    }
  }, [applyState]);

  useEffect(() => {
    loadState();
    const timer = window.setInterval(loadState, 5000);
    return () => window.clearInterval(timer);
  }, [loadState]);

  const submittedNames = useMemo(
    () => (state?.predictions || []).map((prediction) => prediction.displayName),
    [state]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSaving(true);
    try {
      const response = await submitSuytPrediction({
        displayName,
        ouScore: Number(ouScore),
        osuScore: Number(osuScore),
      });
      applyState(response.data);
      setDisplayName('');
      setOuScore('');
      setOsuScore('');
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to save that prediction.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClaimSlot = async (draftSlot) => {
    if (!state?.nextSelector) {
      return;
    }
    setFormError('');
    setIsSaving(true);
    try {
      const response = await claimSuytDraftSlot({
        predictionId: state.nextSelector.id,
        draftSlot,
      });
      applyState(response.data);
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to claim that slot.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleScoreGames = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSaving(true);
    window.sessionStorage.setItem(ADMIN_STORAGE_KEY, adminKey);
    try {
      const response = await submitSuytResults({
        adminKey,
        ouActual: Number(ouActual),
        osuActual: Number(osuActual),
      });
      applyState(response.data);
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to save final scores.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSlots = async () => {
    setFormError('');
    setIsSaving(true);
    window.sessionStorage.setItem(ADMIN_STORAGE_KEY, adminKey);
    try {
      const response = await resetSuytDraftSlots(adminKey);
      applyState(response.data);
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to reset draft slots.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setFormError('');
    setIsSaving(true);
    window.sessionStorage.setItem(ADMIN_STORAGE_KEY, adminKey);
    try {
      const response = await deleteSuytPrediction({ adminKey, id });
      applyState(response.data);
    } catch (error) {
      setFormError(getErrorMessage(error, 'Unable to remove that prediction.'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!state && !loadError) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <p>Loading the draft challenge…</p>
        </div>
      </div>
    );
  }

  const games = state?.games || {};
  const phase = state?.phase || 'open';

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <span className={styles.kicker}>NFL fantasy · 2026 draft order</span>
        <h1 className={styles.title}>Show Us Your TDs</h1>
        <p className={styles.subtitle}>
          Predict OU&apos;s points against UTEP and OSU&apos;s points at Tulsa.
          Closest combined guess picks their draft spot first, then the next closest,
          until every slot is gone.
        </p>

        <div className={styles.games}>
          <article className={`${styles.gameCard} ${styles.ou}`}>
            <div className={styles.gameTeam}>{games.ou?.label || 'Oklahoma vs UTEP'}</div>
            <div className={styles.gameMeta}>
              {games.ou?.kickoff}<br />
              {games.ou?.venue}
            </div>
          </article>
          <article className={`${styles.gameCard} ${styles.osu}`}>
            <div className={styles.gameTeam}>{games.osu?.label || 'Oklahoma State at Tulsa'}</div>
            <div className={styles.gameMeta}>
              {games.osu?.kickoff}<br />
              {games.osu?.venue}
            </div>
          </article>
        </div>

        {state && (
          <div className={styles.statusBar}>
            <div className={styles.statusCount}>
              {state.submittedCount} of {state.maxPredictions} locked in
            </div>
            <div className={styles.statusHint}>
              {phase === 'open' && `${state.remaining} prediction${state.remaining === 1 ? '' : 's'} left. Scores stay hidden until the board is full.`}
              {phase === 'revealed' && 'All predictions are in. Scores are revealed — enter the final totals after Labor Day weekend.'}
              {phase === 'scored' && (state.draftComplete
                ? 'Draft order is locked.'
                : `${state.nextSelector?.displayName} is up to pick a draft slot.`)}
            </div>
          </div>
        )}

        {loadError && <p className={styles.error}>{loadError}</p>}
        {formError && <p className={styles.error}>{formError}</p>}

        {phase === 'open' && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Lock in a prediction</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label htmlFor="suyt-name">Your name</label>
                  <input
                    id="suyt-name"
                    className={styles.input}
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    maxLength={40}
                    required
                    autoComplete="nickname"
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="suyt-ou">OU score vs UTEP</label>
                  <input
                    id="suyt-ou"
                    className={styles.input}
                    type="number"
                    min="0"
                    max="120"
                    step="1"
                    value={ouScore}
                    onChange={(event) => setOuScore(event.target.value)}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="suyt-osu">OSU score at Tulsa</label>
                  <input
                    id="suyt-osu"
                    className={styles.input}
                    type="number"
                    min="0"
                    max="120"
                    step="1"
                    value={osuScore}
                    onChange={(event) => setOsuScore(event.target.value)}
                    required
                  />
                </div>
              </div>
              <div className={styles.submitRow}>
                <p className={styles.hint}>
                  You can match somebody on one game, but the OU + OSU combo has to be unique.
                </p>
                <button className={styles.button} type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving…' : 'Lock it in'}
                </button>
              </div>
            </form>
          </section>
        )}

        {phase === 'open' && submittedNames.length > 0 && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Already in</h2>
            <div className={styles.names}>
              {submittedNames.map((name) => (
                <span key={name} className={styles.nameChip}>{name}</span>
              ))}
            </div>
          </section>
        )}

        {phase !== 'open' && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>
              {phase === 'scored' ? 'Selection order' : 'Predictions'}
            </h2>
            {state.results && (
              <p className={styles.hint}>
                Final scores: OU {state.results.ouActual}, OSU {state.results.osuActual}.
                Closest combined miss picks first. Ties go to whoever submitted earlier.
              </p>
            )}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {phase === 'scored' && <th>#</th>}
                    <th>Name</th>
                    <th>OU</th>
                    <th>OSU</th>
                    {phase === 'scored' && <th>Off by</th>}
                    {phase === 'scored' && <th>Slot</th>}
                  </tr>
                </thead>
                <tbody>
                  {state.predictions.map((prediction) => (
                    <tr
                      key={prediction.id}
                      className={state.nextSelector?.id === prediction.id ? styles.currentRow : undefined}
                    >
                      {phase === 'scored' && <td>{prediction.selectionOrder}</td>}
                      <td>{prediction.displayName}</td>
                      <td>{prediction.ouScore}</td>
                      <td>{prediction.osuScore}</td>
                      {phase === 'scored' && <td>{prediction.error}</td>}
                      {phase === 'scored' && (
                        <td>{prediction.draftSlot ? ORDINAL[prediction.draftSlot - 1] : '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {phase === 'scored' && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>
              {state.draftComplete ? 'Final draft order' : 'Claim a draft slot'}
            </h2>
            {state.draftComplete ? (
              <ol className={styles.finalList}>
                {ORDINAL.map((label, index) => {
                  const owner = state.draftBoard[index + 1];
                  return (
                    <li key={label}>
                      {label} overall — {owner?.displayName || 'Open'}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <>
                <p className={styles.hint}>
                  {state.nextSelector?.displayName} picks next. Tap an open slot to claim it.
                </p>
                <div className={styles.board}>
                  {ORDINAL.map((label, index) => {
                    const slot = index + 1;
                    const owner = state.draftBoard[slot];
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`${styles.slot} ${owner ? styles.slotTaken : ''}`}
                        disabled={Boolean(owner) || isSaving}
                        onClick={() => handleClaimSlot(slot)}
                      >
                        <span className={styles.slotLabel}>{label} overall</span>
                        <span className={styles.slotName}>{owner?.displayName || 'Open'}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        )}

        <button
          type="button"
          className={styles.commissionerToggle}
          onClick={() => setShowCommissioner((open) => !open)}
        >
          {showCommissioner ? 'Hide commissioner tools' : 'Commissioner tools'}
        </button>

        {showCommissioner && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Commissioner</h2>
            <form onSubmit={handleScoreGames}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label htmlFor="suyt-key">Commissioner key</label>
                  <input
                    id="suyt-key"
                    className={styles.input}
                    type="password"
                    value={adminKey}
                    onChange={(event) => setAdminKey(event.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="suyt-ou-actual">OU final</label>
                  <input
                    id="suyt-ou-actual"
                    className={styles.input}
                    type="number"
                    min="0"
                    max="120"
                    value={ouActual}
                    onChange={(event) => setOuActual(event.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="suyt-osu-actual">OSU final</label>
                  <input
                    id="suyt-osu-actual"
                    className={styles.input}
                    type="number"
                    min="0"
                    max="120"
                    value={osuActual}
                    onChange={(event) => setOsuActual(event.target.value)}
                  />
                </div>
              </div>
              <div className={styles.submitRow}>
                <p className={styles.hint}>
                  Scoring unlocks selection order. Removing a prediction clears scores and draft slots.
                </p>
                <button className={styles.button} type="submit" disabled={isSaving}>
                  Save final scores
                </button>
              </div>
            </form>
            <div className={styles.submitRow}>
              <button
                type="button"
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={handleResetSlots}
                disabled={isSaving}
              >
                Reset draft slots
              </button>
            </div>
            {state?.predictions?.length > 0 && (
              <div className={`${styles.names} ${styles.removeRow}`}>
                {state.predictions.map((prediction) => (
                  <button
                    key={prediction.id}
                    type="button"
                    className={`${styles.button} ${styles.dangerButton}`}
                    onClick={() => handleDelete(prediction.id)}
                    disabled={isSaving}
                  >
                    Remove {prediction.displayName}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ShowUsYourTdsPage;
